import EventEmitter from "eventemitter3";

import { Context } from "../../../models/context";
import { Data } from "../../../models/data";
import { EventTypes } from "../../../core/event-bus";
import { LocalizationService } from "../../../services/localization-service";
import { Util } from "../../../utils/util";

/**
 * Represents a banner component that promotes the app and links
 * to the Google Play Store. The banner listens for language change events
 * and updates its text accordingly.
 */
export class AppBanner {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;

    private readonly _containerElement: HTMLElement;
    private _closed = false;

    /**
     * Creates a new AppBanner instance.
     *
     * @param {EventEmitter} eventBus - The event bus used to listen for global events.
     * @param {Data} data - Provides brand and store data for banner content.
     * @param {Context} context - Provides runtime context, such as brand info and URLs.
     * @param {LocalizationService} localizationService - Service used for localized text.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;

        this._containerElement = document.createElement("div");
        this._containerElement.className = "android-banner-root";

        this._render();
        this._eventBusListeners();
    }

    /**
     * Attaches the banner to a parent element in the DOM.
     *
     * @param {HTMLElement} parent - The parent element to attach the banner into.
     * @param {HTMLElement} [before] - Optional sibling element to insert before.
     * @returns {void}
     */
    public attach(parent: HTMLElement, before?: HTMLElement): void {
        if (before)
            parent.insertBefore(this._containerElement, before);
        else
            parent.appendChild(this._containerElement);
    }

    /**
     * Subscribes to relevant events on the event bus (e.g., language changes).
     *
     * @private
     * @returns {void}
     */
    private _eventBusListeners(): void {
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, this._render);
    }

    /**
     * Renders the banner markup based on current brand data and localization.
     * If the banner is closed or missing required settings, clears the container.
     *
     * @private
     * @returns {void}
     */
    private readonly _render = (): void => {
        if (this._closed) {
            this._containerElement.innerHTML = "";
            return;
        }

        const isIos = Util.isiOSNonSafari();

        const settings = this._data.getBrandProperty("Settings").OOS;
        const androidPackageName = settings.appBannerPlayStorePackageName;
        const iosPackageName = settings.appBannerAppStoreId;
        if ((isIos && !iosPackageName) || !androidPackageName) {
            this._containerElement.innerHTML = "";
            return;
        }

        const url = isIos
            ? `https://apps.apple.com/app/id${iosPackageName}`
            : `https://play.google.com/store/apps/details?id=${androidPackageName}`

        const promptText = settings.appBannerPrompText;
        const brandName = this._context.getProperty("brandInfo").Name;
        const contentFolderUrl = this._context.getProperty("publicStorageUrl");

        // Build the banner markup
        this._containerElement.innerHTML = `
            <aside class="app-banner" aria-label="${this._localizationService.names("APP_DOWNLOAD_BANNER")}" style="display: flex;">
                <button id="btn_app_banner_close" class="icon-cancel" aria-label="${this._localizationService.names("CLOSE_BANNER")}"></button>

                <div>
                    <img src="${contentFolderUrl}/online3/svg/appicon.svg" alt="${this._localizationService.names("APP_ICON_FOR", undefined, brandName)}" class="app-icon">
                </div>

                <div>
                    <h2>${brandName}</h2>
                    <p class="description">${promptText}</p>
                </div>

                <a href="${url}" class="button">${this._localizationService.names("VIEW")}</a>
            </aside>
        `;

        const closeBtn = this._containerElement.querySelector<HTMLButtonElement>("#btn_app_banner_close");
        closeBtn?.addEventListener("click", this._hide);
    }

    /**
     * Hides the banner by setting its state to closed and updating display style.
     *
     * @private
     * @returns {void}
     */
    private readonly _hide = (): void => {
        if (this._containerElement) {
            this._closed = true;
            this._containerElement.style.display = "none";
            document.body.classList.remove("android-app-banner-visible");
        }
    }
}